import { api } from '@api/api';
import { PaginatedResult } from '@api/types/paginated-result';
import { TaskDetailListItemResponse } from '@api/types/task-detail/task-detail-list-item.response';
import { TaskDetailListRequest } from '@api/types/task-detail/task-detail-list.request';
import { TaskDetailResource } from '@api/types/task-detail/task-detail.resource';
import { CreateTaskDetailRequest } from '@api/types/task-detail/create-task-detail.request';
import { makeError } from '@api/types/api-error';
import { UpdateTaskDetailRequest } from '@api/types/task-detail/update-task-detail.request';
import { TaskDetailStats } from '@api/types/task-detail/task-detail-stats';
import { CompleteTaskRequest } from '@api/types/task-detail/complete-task.request';

const companyTaskDetailApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTasks: build.query<
      PaginatedResult<TaskDetailListItemResponse, TaskDetailStats>,
      TaskDetailListRequest
    >({
      query: ({ caseId, ...params }) => ({
        url:
          caseId == null ? 'company/taskDetail' : `case/${caseId}/taskDetail`,
        method: 'GET',
        params,
      }),
      providesTags: (response) =>
        response
          ? [
              ...response!.model.map((data) => ({
                id: data.taskDetailId,
                type: 'TaskDetail' as const,
              })),
              'TaskDetail',
            ]
          : ['TaskDetail'],
    }),
    getTaskDetailForCompany: build.query<TaskDetailResource, number>({
      query: (id) => ({
        url: `company/taskDetail/${id}`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [{ id: result.taskDetailId, type: 'TaskDetail' as const }]
          : [],
    }),
    createTask: build.mutation<TaskDetailResource, CreateTaskDetailRequest>({
      query: (body) => ({
        url: 'company/taskDetail',
        method: 'POST',
        body,
      }),
      transformErrorResponse: (error) =>
        makeError<CreateTaskDetailRequest>(error),
      invalidatesTags: ['TaskDetail', 'CaseTotals'],
    }),
    updateTaskForCompany: build.mutation<
      TaskDetailResource,
      UpdateTaskDetailRequest
    >({
      query: (body) => ({
        url: 'company/taskDetail',
        method: 'PUT',
        body,
      }),
      transformErrorResponse: (error) =>
        makeError<UpdateTaskDetailRequest>(error),
      invalidatesTags: (_, __, arg) => [
        { id: arg.taskDetailID, type: 'TaskDetail' as const },
      ],
    }),
    completeTaskForCompany: build.mutation<void, CompleteTaskRequest>({
      query: (body) => ({
        url: 'company/taskDetail/complete',
        method: 'PUT',
        body,
      }),
      transformErrorResponse: (error) => makeError(error),
      invalidatesTags: (_, __, arg) => [
        { id: arg.taskDetailId, type: 'TaskDetail' as const },
        'CaseTotals',
      ],
    }),
    openTaskForCompany: build.mutation<void, CompleteTaskRequest>({
      query: (body) => ({
        url: 'company/taskDetail/open',
        method: 'PUT',
        body,
      }),
      transformErrorResponse: (error) => makeError(error),
      invalidatesTags: (_, __, arg) => [
        { id: arg.taskDetailId, type: 'TaskDetail' as const },
        'CaseTotals',
      ],
    }),
  }),
});

export const {
  useCompleteTaskForCompanyMutation,
  useCreateTaskMutation,
  useGetTaskDetailForCompanyQuery,
  useGetTasksQuery,
  useUpdateTaskForCompanyMutation,
  useOpenTaskForCompanyMutation,
} = companyTaskDetailApi;
